import { useCallback, useEffect, useRef, useState } from 'react';
import { api, endpoints } from '../../../api';
import { ApiError, uuid } from '../../../api/client';
import type { SpeechOrder } from '../../../api/speech';
import { record } from '../../studio/studio-content';
interface MoreIntent {
  owner: number;
  root: number;
  source: number;
  key: string;
  body: { group_key: string; expected_price: number };
  child?: number;
  unknown: boolean;
}
interface Chain {
  owner: number;
  root: number;
  ids: number[];
  pending: MoreIntent | null;
}
const storageKey = (owner: number, root: number) => `steos:text:more:${owner}:${root}`;
const positive = (v: unknown): v is number => Number.isSafeInteger(v) && Number(v) > 0;
export function readTextChain(owner: number, root: number): Chain {
  const raw = sessionStorage.getItem(storageKey(owner, root));
  if (!raw) return { owner, root, ids: [], pending: null };
  const value = JSON.parse(raw) as Chain,
    p = value.pending;
  if (
    value.owner !== owner ||
    value.root !== root ||
    !Array.isArray(value.ids) ||
    value.ids.length > 50 ||
    !value.ids.every(positive) ||
    (p &&
      (p.owner !== owner ||
        p.root !== root ||
        !positive(p.source) ||
        !/^[-0-9a-f]{36}$/i.test(p.key) ||
        typeof p.body?.group_key !== 'string' ||
        !Number.isFinite(p.body.expected_price) ||
        p.body.expected_price < 0 ||
        typeof p.unknown !== 'boolean' ||
        (p.child !== undefined && !positive(p.child))))
  )
    throw Error('invalid_more_intent');
  return value;
}
/** Каждая группа — самостоятельный канонический заказ, ключ сохраняется до известного исхода. */
export function useTextMore(owner: number | undefined, root: SpeechOrder | null, cap: unknown) {
  const [loaded, setLoaded] = useState<{ identity: string; chain: Chain; orders: SpeechOrder[] } | null>(null);
  const [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [blocked, setBlocked] = useState(false);
  const live = useRef(true),
    mutex = useRef(false),
    revision = useRef(0);
  const identity = `${owner}:${root?.id}`,
    policy = record(cap);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  useEffect(() => {
    if (!owner || !root) return;
    const controller = new AbortController(),
      version = ++revision.current;
    void (async () => {
      try {
        const chain = readTextChain(owner, root.id);
        if ((await endpoints.me()).user.id !== owner || controller.signal.aborted) return;
        const orders = await Promise.all(chain.ids.map(id => endpoints.speech.get(id, controller.signal)));
        if (!controller.signal.aborted && version === revision.current) setLoaded({ identity, chain, orders });
      } catch {
        if (!controller.signal.aborted) setBlocked(true);
      }
    })();
    return () => controller.abort();
  }, [owner, root, identity]);
  const current = loaded?.identity === identity ? loaded : null;
  const persist = useCallback(
    (chain: Chain, orders = current?.orders || []) => {
      sessionStorage.setItem(storageKey(chain.owner, chain.root), JSON.stringify(chain));
      if (live.current) setLoaded({ identity, chain, orders });
    },
    [current, identity],
  );
  async function run(replay = false) {
    if (!owner || !root || !current || blocked || mutex.current || (!replay && policy.available !== true)) return;
    mutex.current = true;
    setBusy(true);
    setError('');
    let chain = current.chain;
    try {
      if ((await endpoints.me()).user.id !== owner) throw Error('owner_changed');
      let p = chain.pending;
      if (!p) {
        const source = current.orders.findLast(v => v.status === 'done') ?? root;
        const group = typeof policy.group_key === 'string' ? policy.group_key : '';
        if (source.status !== 'done' || !group || typeof policy.expected_price !== 'number' || policy.expected_price < 0)
          throw Error('unavailable');
        p = {
          owner,
          root: root.id,
          source: source.id,
          key: uuid(),
          body: { group_key: group, expected_price: policy.expected_price },
          unknown: false,
        };
        chain = { ...chain, pending: p };
        persist(chain);
      }
      // Сохраняем неизвестность ДО транспорта: закрытие окна не разрешает новый ключ.
      const wasUnknown = p.unknown;
      p = { ...p, unknown: true };
      chain = { ...chain, pending: p };
      persist(chain);
      if (!p.child) {
        try {
          const response = await api.request<{ operation_order_id: number }>(`/app/orders/${p.source}/more`, {
            method: 'POST',
            body: p.body,
            idempotency: p.key,
          });
          if (!positive(response.operation_order_id)) throw Error('invalid_receipt');
          p = { ...p, child: response.operation_order_id };
          chain = { ...chain, pending: p };
          persist(chain);
        } catch (failure) {
          if (!wasUnknown && failure instanceof ApiError && [400, 402, 404, 410, 422].includes(failure.status)) {
            chain = { ...chain, pending: null };
            persist(chain);
          }
          throw failure;
        }
      }
      const next = await endpoints.speech.get(p.child!);
      if ((await endpoints.me()).user.id !== owner) throw Error('owner_changed');
      if (['done', 'failed'].includes(next.status)) {
        const ids = chain.ids.includes(next.id) ? chain.ids : [...chain.ids, next.id];
        const orders = [...current.orders.filter(v => v.id !== next.id), next];
        persist({ ...chain, ids, pending: null }, orders);
      }
    } catch {
      if (live.current) setError('Не удалось подтвердить дополнительные варианты. Проверьте тот же запрос.');
    } finally {
      mutex.current = false;
      if (live.current) setBusy(false);
    }
  }
  // Poll только GET существующего child. Неизвестный POST восстанавливает исключительно ручной replay.
  useEffect(() => {
    const p = current?.chain.pending;
    if (!p?.child || !owner || !root || !current) return;
    const snapshot = current;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        if ((await endpoints.me()).user.id !== owner || controller.signal.aborted) return;
        const next = await endpoints.speech.get(p.child!, controller.signal);
        if (controller.signal.aborted) return;
        if (['done', 'failed'].includes(next.status)) {
          const ids = snapshot.chain.ids.includes(next.id) ? snapshot.chain.ids : [...snapshot.chain.ids, next.id];
          persist({ ...snapshot.chain, ids, pending: null }, [...snapshot.orders.filter(v => v.id !== next.id), next]);
        } else if (next.status !== 'uncertain') timer = setTimeout(() => void poll(), 2000);
      } catch {
        if (!controller.signal.aborted) setError('Не удалось подтвердить дополнительные варианты. Проверьте тот же запрос.');
      }
    };
    timer = setTimeout(() => void poll(), 2000);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [current, owner, root, persist]);
  return {
    orders: current?.orders || [],
    pending: current?.chain.pending || null,
    busy,
    blocked: blocked || !current,
    error,
    request: () => run(),
    retry: () => run(true),
  };
}
